import { t } from '../../../i18next';
import { getDynamicLabel } from '../../../shared/utils/labels';
import posthog from 'posthog-js';
import { getAllDropdowns } from '../../../shared/services/allDropdownsRegistry';
import { AllDropdowns } from '../../../shared/types';

type ExtraValidatorResult = {
  isValid: boolean;
  errors: { [key: string]: { field: string; msg: string } };
};

const hasValue = (v: unknown) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return !Number.isNaN(v);
  return true;
};

const KEY_CATEGORY_MAP: Record<string, string> = {
  psp_c_a_element_cmm: 'CMM',
  psp_c_a_element_ncp: 'NCP',
  psp_c_a_element_femi: 'Hard Tooling',
};

// Centralized Error Appender
const addRequiredError = (
  missingKey: string,
  allColumnDefs: Array<any>,
  language: string,
  result: ExtraValidatorResult,
  msg: string = 'required',
) => {
  result.isValid = false;
  const fieldLabel = getDynamicLabel(missingKey, allColumnDefs, language) || missingKey;

  // If a custom validation message is passed, append the field label dynamically right here.
  const finalMsg = msg === 'required' ? msg : `${fieldLabel} ${msg}`;

  result.errors[missingKey] = {
    field: fieldLabel,
    msg: finalMsg,
  };
};

// Helper: validate a PSP element and budget pairs
const checkPairs = (
  row: Record<string, any>,
  elementKey: string,
  budgetKey: string,
  allColumnDefs: Array<any>,
  language: string,
  result: ExtraValidatorResult,
  pairingMsg: string,
) => {
  const elementVal = row[elementKey];
  const budgetVal = row[budgetKey];
  const elementFilled = hasValue(elementVal);
  const budgetFilled = hasValue(budgetVal);
  const isIncomplete = !elementFilled && budgetFilled;
  if (!isIncomplete) return;

  result.isValid = false;
  const missingKey = elementKey;
  const fieldLabel = getDynamicLabel(missingKey, allColumnDefs, language);

  result.errors[missingKey] = {
    field: fieldLabel,
    msg: `${pairingMsg} ${KEY_CATEGORY_MAP[elementKey]}.`,
  };
};

const canUpdateField = (access: any, key: string) => access?.[key]?.accessLevel === 'U';

const validatePspPairs = (
  row: Record<string, any>,
  pspEnabled: boolean,
  allColumnDefs: Array<any>,
  language: string,
  result: ExtraValidatorResult,
) => {
  if (!pspEnabled) return;

  const pairingMsg = t('npiSteering.psp_element_missing');
  const pairConfigs: Array<{ elementKey: string; budgetKey: string }> = [
    { elementKey: 'psp_c_a_element_cmm', budgetKey: 'cmmhrs' },
    { elementKey: 'psp_c_a_element_ncp', budgetKey: 'capex_stunden' },
    { elementKey: 'psp_c_a_element_femi', budgetKey: 'hardtooling_euro' },
  ];

  pairConfigs.forEach(({ elementKey, budgetKey }) => {
    checkPairs(row, elementKey, budgetKey, allColumnDefs, language, result, pairingMsg);
  });
};

const getDeltaFaiId = (allColumnDefs: Array<any>) =>
  allColumnDefs
    .find((col) => col.attrInternalName === 'inspection_planning_need')
    ?.valueOptions?.find((opt) => opt.label === 'Delta FAI')?.value;

const validateFaiFields = (
  row: Record<string, any>,
  allColumnDefs: Array<any>,
  language: string,
  access: any,
  result: ExtraValidatorResult,
) => {
  const isDeltaFaiSelected = row['inspection_planning_need'] === getDeltaFaiId(allColumnDefs);
  if (canUpdateField(access, 'delta_fai_ref') && isDeltaFaiSelected && !hasValue(row.delta_fai_ref)) {
    addRequiredError('delta_fai_ref', allColumnDefs, language, result);
  }

  if (
    canUpdateField(access, 'reason_for_fai_deviation') &&
    hasValue(row.fai_type_deviation) &&
    !hasValue(row.reason_for_fai_deviation)
  ) {
    addRequiredError('reason_for_fai_deviation', allColumnDefs, language, result);
  }
};

const validatePpvComment = (
  row: Record<string, any>,
  allColumnDefs: Array<any>,
  language: string,
  access: any,
  allDropdowns: AllDropdowns,
  result: ExtraValidatorResult,
) => {
  if (!canUpdateField(access, 'ppv_status') || !hasValue(row.ppv_status)) return;

  const ppvStatusLabel = getDropdownLabel(allDropdowns, 'ppv_status', row.ppv_status);
  if (!ppvStatusLabel || ppvStatusLabel === 'Launched' || hasValue(row.ppv_status_comment)) return;

  addRequiredError('ppv_status_comment', allColumnDefs, language, result);
};

const validateRoutingGroup = (
  row: Record<string, any>,
  allColumnDefs: Array<any>,
  language: string,
  result: ExtraValidatorResult,
) => {
  if (!hasValue(row.plangruppe)) return;

  const plangruppeStr = String(row.plangruppe).trim();
  if (!/^\d{8}$/.test(plangruppeStr)) {
    addRequiredError(
      'plangruppe',
      allColumnDefs,
      language,
      result,
      t('npiSteering.routing_group_validation'),
    );
  }
};

const validateRoutingGroupCounter = (
  row: Record<string, any>,
  allColumnDefs: Array<any>,
  language: string,
  result: ExtraValidatorResult,
) => {
  if (!hasValue(row.pgz)) return;

  const pgzStr = String(row.pgz).trim();
  if (!isValidRoutingGroupCounter(pgzStr)) {
    addRequiredError(
      'pgz',
      allColumnDefs,
      language,
      result,
      t('npiSteering.routing_group_counter_validation'),
    );
  }
};

function isValidRoutingGroupCounter(value: string): boolean {
  if (!/^\d{1,2}$/.test(value)) return false;
  if (value === '00') return true;
  if (/^0\d$/.test(value)) return false;

  const numericValue = Number(value);
  return !Number.isNaN(numericValue) && numericValue >= 0 && numericValue <= 99;
}

const validateSapRoutingFields = (
  row: Record<string, any>,
  allColumnDefs: Array<any>,
  language: string,
  access: any,
  result: ExtraValidatorResult,
) => {
  const canValidateRoutingGroup = canUpdateField(access, 'plangruppe');
  const canValidateRoutingGroupCounter = canUpdateField(access, 'pgz');

  if (canValidateRoutingGroup) {
    validateRoutingGroup(row, allColumnDefs, language, result);
  }

  if (canValidateRoutingGroupCounter) {
    validateRoutingGroupCounter(row, allColumnDefs, language, result);
  }
};

export async function extraValidator({
  row,
  allColumnDefs,
  language,
  access,
}: {
  row: Record<string, any>;
  rows: Array<any>;
  tableDetails: any;
  allColumnDefs: Array<any>;
  language: string;
  originalRows: Array<any>;
  access: any;
  getAllDropdowns: () => AllDropdowns;
}): Promise<ExtraValidatorResult> {
  const result: ExtraValidatorResult = { isValid: true, errors: {} };

  const pspEnabled = posthog.isFeatureEnabled('newPspManagement') === true;
  validatePspPairs(row, pspEnabled, allColumnDefs, language, result);
  validateFaiFields(row, allColumnDefs, language, access, result);

  const allDropdowns: AllDropdowns = getAllDropdowns();
  validatePpvComment(row, allColumnDefs, language, access, allDropdowns, result);
  validateSapRoutingFields(row, allColumnDefs, language, access, result);

  return result;
}

/**
 * Resolves the display label for a dropdown value from the dropdown registry.
 */
function getDropdownLabel(allDropdowns: AllDropdowns | undefined, key: string, value: unknown) {
  const options = allDropdowns?.[key];
  if (!options?.length) return undefined;
  return options.find((opt) => opt?.value == (value as any))?.label;
}